import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, Paper } from '@mui/material';
import DHDialog from '../dh-dialog/dh-dialog.component';
import { OrderTag } from 'models/tags';
import { CheckCircle } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { orderTagsTemplate } from 'apollo/templates/order-tags';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type AssignTagInOrderDialogProps = {
    open: boolean;
    tags?: OrderTag[];
    onDialogClose: () => void;
    onAddUpdate: (tags: OrderTag[]) => void;
    rerender?: boolean;
};

const AssignTagInOrderDialog: FC<AssignTagInOrderDialogProps> = ({
    open,
    onDialogClose,
    onAddUpdate,
    tags,
    rerender,
}: AssignTagInOrderDialogProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [selectedTags, setSelectedTags] = useState<OrderTag[]>([]);
    const [orderTags, setOrderTags] = useState<OrderTag[]>([]);

    useQuery(orderTagsTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ orderTags }) {
            setOrderTags(orderTags);
        },
    });

    useEffect(() => {
        if (tags) {
            setSelectedTags(tags);
        } else {
            setSelectedTags([]);
        }
    }, [tags, rerender]);

    const handleClearTags = () => {
        setSelectedTags([]);
    };

    const handleDialogClose = () => {
        onDialogClose();
        setSelectedTags([]);
    };

    const handleToggleTag = (tag: OrderTag) => () => {
        const exists = selectedTags.some((t) => t._id === tag._id);

        if (exists) {
            setSelectedTags(selectedTags.filter((t) => t._id !== tag._id)); // remove
        } else {
            setSelectedTags([...selectedTags, tag]); // add
        }
    };

    const handleAddUpdateTag = () => {
        onAddUpdate(selectedTags);
        handleDialogClose();
    };

    return (
        <DHDialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={handleDialogClose}
            title={tags?.length ? translate('lab-settings:EDIT_TAG') : translate('lab-settings:ADD_TAG')}
            actions={
                <>
                    {tags?.length ? (
                        <Button
                            disabled={!selectedTags}
                            variant="contained"
                            onClick={handleAddUpdateTag}
                            data-test="assign-tag-in-order-dialog.update-btn"
                        >
                            {translate('common:UPDATE')}
                        </Button>
                    ) : (
                        <Button
                            disabled={!selectedTags}
                            variant="contained"
                            onClick={handleAddUpdateTag}
                            data-test="assign-tag-in-order-dialog.add-btn"
                        >
                            {translate('common:ADD')}
                        </Button>
                    )}
                </>
            }
        >
            <Paper variant="outlined">
                {!orderTags?.length ? (
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('lab-settings:NO_TAG_CONFIGURED')}
                    </Alert>
                ) : undefined}
                {orderTags?.length > 0 ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1.5,
                            p: 1.5,
                        }}
                    >
                        {orderTags.map((tg, index) => {
                            const isSelected = selectedTags.some((t) => t._id === tg._id);
                            return (
                                <Chip
                                    key={index}
                                    clickable
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    icon={isSelected ? <CheckCircle sx={{ color: '#fff !important' }} /> : undefined}
                                    label={tg.name}
                                    sx={{
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        borderWidth: 2,
                                        '& .MuiChip-label': { fontWeight: 'bold' },
                                        ...(isSelected
                                            ? {
                                                  backgroundColor: tg.color,
                                                  borderColor: tg.color,
                                                  color: '#ffffff',
                                              }
                                            : {
                                                  borderColor: tg.color,
                                                  color: tg.color,
                                              }),
                                        '&:hover': {
                                            opacity: 0.75,
                                            backgroundColor: isSelected ? tg.color : 'transparent',
                                        },
                                    }}
                                    size="medium"
                                    onClick={handleToggleTag(tg)}
                                />
                            );
                        })}
                    </Box>
                ) : undefined}
            </Paper>
        </DHDialog>
    );
};

export default AssignTagInOrderDialog;
